import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, model, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PACIMPgpWithProvider } from '@core/model';
import { MemberPacService, ServicingFacilitiesPacimService } from '@core/service';
import { PROMEDICA_AUTH_FIRST_MESSAGE, PROMEDICA_AUTH_SECOND_MESSAGE } from '@shared/constants';
import { AuthorizationFacilityContactPAC, ServicingFacilityContactDetailsPAC, ServicingFacilityExistingData, ServicingFacilityPAC } from '@shared/model';
import { isUndefined, mergeProvidersWithPgps } from '@shared/utility';
import { debounce } from 'lodash-es';
import { ConfirmationService } from 'primeng/api';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';

@Component({
    selector: 'servicing-facility-tab',
    templateUrl: './servicing-facility-tab.component.html',
    styleUrls: ['./servicing-facility-tab.component.scss'],
    standalone: false
})
export class ServicingFacilityTabComponent implements OnInit {
  @Input() servicingFacility!: ServicingFacilityPAC | null;
  @Input() tenantId!: string;
  @Input() tenantName!: string;
  @Input() isExternalFlow!: boolean;
  @Input() facilityType!: string;
  @Input() memberId!: string;
  @Input() isGoldCardVisible = false;

  @Output() autoUpdate = new EventEmitter<ServicingFacilityPAC>();
  @Output() backStep = new EventEmitter<void>();
  @Output() cancelProcess = new EventEmitter<boolean>();
  @Output() deleteFacility = new EventEmitter<void>();
  @Output() submitStep = new EventEmitter<ServicingFacilityPAC>();

  loading = false;
  contactDetails?: ServicingFacilityContactDetailsPAC;
  userFacilities?: PACIMPgpWithProvider[];
  differentFacility?: boolean;
  newFacility?: AuthorizationFacilityContactPAC;
  npiSearchFacilities?: PACIMPgpWithProvider[];
  selectedUserFacilityIndex?: number;
  selectedNpiFacilityIndex?: number;
  selectedUserFacilityPgpId?: string;
  selectedNpiFacilityPgpId?: string;
  isProMedicaMember = false;
  updateCounter? = model<number>();

  get canSubmit(): boolean {
    return !this.differentFacility
      ? !!this.userFacilities && !isUndefined(this.selectedUserFacilityIndex) && !!this.selectedUserFacilityPgpId && !!this.contactDetails
      : (
        (!!this.npiSearchFacilities && !isUndefined(this.selectedNpiFacilityIndex) && !!this.selectedNpiFacilityPgpId && !!this.contactDetails)
        || !!this.newFacility
      );
  }

  private autoUpdateDebounced = debounce(() => {
    this.autoUpdate.emit(this.serializeSubmitData())
  }, 500);

  private cdRef = inject(ChangeDetectorRef);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly facilitiesService = inject(ServicingFacilitiesPacimService);
  private readonly memberPacService = inject(MemberPacService);

  ngOnInit(): void {
    this.getUserFacilities()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.servicingFacility) {
          if (this.servicingFacility.provider && this.servicingFacility.provider.servicingProviderPgpsDTO.facilityServiceType !== this.facilityType) {
            this.deleteFacility.emit();
          } else {
            this.setTabState();
          }
        }
      });

    // Check if member is ProMedica
    this.memberPacService.isProMedicaMember(this.tenantId, this.memberId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (isEligible) => {
          this.isProMedicaMember = isEligible;
        },
        error: (err) => {
          // Pushing error to logs instead of UI display since ProMedica is for a small subset of users
          console.error('Unable to determine ProMedica Eligbility', err);
        }
      });
  }

  onCancel(): void {
    this.cancelProcess.emit(false);
  }

  // Needed to bypass cancel modal for ProMedica flow
  onDirectCancel(): void {
    this.cancelProcess.emit(true);
  }

  onClickBack(): void {
    this.backStep.emit();
  }

  onDetailsChanged(
    details: ServicingFacilityContactDetailsPAC | null
  ): void {
    this.contactDetails = details;

    if (this.canSubmit) {
      this.autoUpdateDebounced();
    }
  }

  onNewFacilityUpdated(): void {
    if (this.canSubmit) {
      this.autoUpdateDebounced();
    }
  }

  onSearch(npi: string): void {
    if (this.isProMedicaMember) {
      this.facilitiesService.isNPIExcludedForProMedica(this.tenantId, npi)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (isExcluded) => {
            if (!isExcluded) {
              this.searchForFacility(npi);
            } else {
              this.updateCounter.update(num => num + 1);
              this.displayExcludedNPIMessage(this.updateCounter());
            }
          },
          error: (err) => {
            // Pushing error to logs instead of UI display since ProMedica is for a small subset of users
            console.error('Unable to determine if Servicing Facility is ProMedica excluded', err);
          }
        });
    } else {
      this.searchForFacility(npi);
    }
  }

  private searchForFacility(npi: string) {
    this.resetDifferentState();
    this.loading = true;

    this.facilitiesService.getByNpi(
      this.tenantId,
      this.memberId,
      npi,
      this.facilityType
    ).pipe(
      catchError(() => {
        return of([]);
      }),
      finalize(() => {
        this.loading = false;

        this.cdRef.markForCheck();
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(facilities => {
      this.npiSearchFacilities = mergeProvidersWithPgps(facilities);

      if (this.npiSearchFacilities.length === 1) {
        this.selectedNpiFacilityIndex = 0;
        this.selectedNpiFacilityPgpId = this.npiSearchFacilities[0].providerPgpId;

        if (this.canSubmit) {
          this.autoUpdate.emit(this.serializeSubmitData());
        }
      }
    });
  }

  onSelectedFacility(
    index: number,
    facilities: PACIMPgpWithProvider[]
  ): void {
    if (facilities === this.npiSearchFacilities) {
      this.selectedNpiFacilityIndex = index;
      this.selectedNpiFacilityPgpId = this.npiSearchFacilities[index].providerPgpId;
    } else {
      this.selectedUserFacilityIndex = index;
      this.selectedUserFacilityPgpId = this.userFacilities[index].providerPgpId;
    }

    if (this.canSubmit) {
      this.autoUpdate.emit(this.serializeSubmitData());
    }
  }

  onSubmit(): void {
    this.submitStep.emit(this.serializeSubmitData());
  }

  switchFacilityDifference(): void {
    if (this.differentFacility && this.userFacilities?.length > 1) {
      this.selectedUserFacilityIndex = undefined;
      this.selectedUserFacilityPgpId = undefined;
    }

    if (!this.differentFacility) {
      this.resetDifferentState();
    }
  }

  private getUserFacilities(): Observable<void> {
    this.loading = true;

    return this.facilitiesService.getByCurrentUser(
      this.tenantId,
      this.memberId,
      this.facilityType
    ).pipe(
      tap(facilities => {
        this.userFacilities = mergeProvidersWithPgps(facilities);

        if (this.userFacilities.length === 0) {
          this.differentFacility = true;
        }

        if (this.userFacilities.length === 1) {
          this.selectedUserFacilityIndex = 0;
          this.selectedUserFacilityPgpId = this.userFacilities[0].providerPgpId;

          if (this.canSubmit) {
            this.autoUpdate.emit(this.serializeSubmitData());
          }
        }
      }),
      catchError(() => {
        this.differentFacility = true;
        this.userFacilities = [];

        return of();
      }),
      finalize(() => {
        this.loading = false;

        this.cdRef.markForCheck();
      }),
      map(() => undefined)
    );
  }

  private resetDifferentState(): void {
    this.npiSearchFacilities = undefined;
    this.selectedNpiFacilityIndex = undefined;
    this.selectedNpiFacilityPgpId = undefined;
    this.newFacility = undefined;
  }

  private serializeFacility(
    facility: PACIMPgpWithProvider
  ): ServicingFacilityExistingData {
    const result: ServicingFacilityExistingData = {
      provider: {
        servicingProviderAccountId: facility.providerAccountId,
        servicingProviderPgpId: facility.providerPgpId,
        servicingProviderPgpsDTO: facility,
        isNpiFacility: this.differentFacility,
      },
      facilityContact: this.contactDetails,
      userWithoutFacilities: !this.userFacilities.length
    };

    if (!this.differentFacility) {
      result.provider.selectedUserFacilityIndex = this.selectedUserFacilityIndex;
    }

    return result;
  }

  private serializeSubmitData(): ServicingFacilityPAC {
    if (!this.differentFacility) {
      return this.serializeFacility(
        this.userFacilities[this.selectedUserFacilityIndex]
      );
    } else {
      return this.npiSearchFacilities.length
        ? this.serializeFacility(
          this.npiSearchFacilities[this.selectedNpiFacilityIndex]
        )
        : {
          facilityContact: this.newFacility,
          userWithoutFacilities: !this.userFacilities.length
        };
    }
  }

  private setTabState(): void {
    const contactDetails = this.servicingFacility.facilityContact;

    this.contactDetails = {
      contactFax: contactDetails.contactFax,
      contactName: contactDetails.contactName,
      contactPhone: contactDetails.contactPhone,
      contactPhoneExtension: contactDetails.contactPhoneExtension
    };

    const existingProvider = this.servicingFacility.provider;

    if (!!existingProvider) {
      if (existingProvider.isNpiFacility) {
        this.differentFacility = true;
        this.npiSearchFacilities = [existingProvider.servicingProviderPgpsDTO];
        this.selectedNpiFacilityIndex = 0;
        this.selectedNpiFacilityPgpId = existingProvider.servicingProviderPgpId;
      } else {
        this.selectedUserFacilityIndex = existingProvider.selectedUserFacilityIndex;
        this.selectedUserFacilityPgpId = existingProvider.servicingProviderPgpId;
      }
    } else {
      this.newFacility = contactDetails as AuthorizationFacilityContactPAC;
      this.npiSearchFacilities = [];
      this.differentFacility = true;
    }

    this.cdRef.markForCheck();
  }

  private displayExcludedNPIMessage(counter: number): void {
    if (counter === 1) {
      this.confirmationService.confirm({
        header: 'Excluded NPI',
        message: PROMEDICA_AUTH_FIRST_MESSAGE,
        closable: false,
        accept: () => {
          this.confirmationService.close();
        },
        acceptLabel: 'Provide Different NPI',
        reject: () => {
          this.confirmationService.close();
          this.onDirectCancel();
        },
        rejectLabel: 'Cancel Request',
        rejectButtonStyleClass: 'p-button-outlined p-button-danger',
      });
    } else {
      this.confirmationService.confirm({
        header: 'Excluded NPI',
        message: PROMEDICA_AUTH_SECOND_MESSAGE,
        closable: false,
        accept: () => {
          this.confirmationService.close();
          this.onDirectCancel();
        },
        acceptLabel: 'Cancel Request',
        acceptButtonStyleClass: 'p-button-danger',
        rejectVisible: false,
      });
    }
  }
}
